import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Moon, Sun, User, ArrowUpRight } from "lucide-react";
import { useAuth } from "../auth/AuthContext";
import AuthModal from "../auth/AuthModal";
import AccountManager from "../auth/AccountManager";

const BLOG_THEME_KEY = "mc_blog_theme";

export default function BlogAboutPage() {
  const { user } = useAuth();
  const [authOpen, setAuthOpen] = useState(false);
  const [theme, setTheme] = useState(() => {
    if (typeof window === "undefined") return "light";
    return localStorage.getItem(BLOG_THEME_KEY) || "light";
  });

  useEffect(() => {
    document.documentElement.classList.remove("dark");
    window.scrollTo(0, 0);
    document.title = "About \u2022 Malicke Case";
    return () => {
      document.documentElement.classList.add("dark");
      document.title = "Malicke Case";
    };
  }, []);

  useEffect(() => {
    try { localStorage.setItem(BLOG_THEME_KEY, theme); } catch (_) {}
  }, [theme]);

  const isDark = theme === "dark";
  const pageBg = isDark ? "#050506" : "#E6E2DA";

  return (
    <div
      className="blog-theme min-h-screen"
      data-blog-theme={isDark ? "dark" : "light"}
      style={{
        background: pageBg,
        fontFamily: "'Inter', system-ui, sans-serif",
        transition: "background 200ms ease",
        padding: "32px 16px 64px"
      }}
    >
      {/* Top controls */}
      <div className="mx-auto flex justify-end gap-[10px] mb-1" style={{ width: "100%", maxWidth: 980 }}>
        <button
          onClick={() => setTheme(isDark ? "light" : "dark")}
          className="inline-flex items-center gap-2 rounded-full px-[14px] py-[6px]"
          style={{
            fontSize: 11,
            letterSpacing: "0.06em",
            border: "1px solid var(--blog-border-primary)",
            background: "var(--blog-bg-primary)",
            color: "var(--blog-text-secondary)",
            cursor: "pointer"
          }}
        >
          {isDark ? <Sun className="w-3 h-3" /> : <Moon className="w-3 h-3" />}
          {isDark ? "Light mode" : "Dark mode"}
        </button>
      </div>

      {/* Card */}
      <div
        className="mx-auto overflow-hidden"
        style={{
          width: "100%",
          maxWidth: 980,
          background: "var(--blog-bg-primary)",
          borderRadius: 12,
          border: "0.5px solid var(--blog-border-primary)",
          transition: "background 200ms ease"
        }}
      >
        {/* Nav */}
        <nav
          className="px-7 pt-[14px] pb-[14px]"
          style={{ borderBottom: "0.5px solid var(--blog-border-tertiary)" }}
        >
          {/* Row 1: brand + account */}
          <div className="flex items-center justify-between">
            <Link
              to="/"
              className="font-display font-medium uppercase tracking-[0.12em]"
              style={{ fontSize: 12, color: "var(--blog-text-secondary)" }}
            >
              Malicke Case
            </Link>
            {/* Desktop center links */}
            <div className="hidden sm:flex gap-6">
              <span className="uppercase" style={{ fontSize: 11, letterSpacing: "0.06em", color: "var(--blog-text-primary)" }}>About</span>
              <Link to="/blog" className="uppercase" style={{ fontSize: 11, letterSpacing: "0.06em", color: "var(--blog-text-tertiary)" }}>Journal</Link>
              <Link to="/blog/contact" className="uppercase" style={{ fontSize: 11, letterSpacing: "0.06em", color: "var(--blog-text-tertiary)" }}>Contact</Link>
            </div>
            {user ? (
            <AccountManager />
            ) : (
              <button
                onClick={() => setAuthOpen(true)}
                className="inline-flex items-center gap-1.5 rounded-full"
                style={{ fontSize: 11, letterSpacing: "0.04em", padding: "6px 14px", border: "0.5px solid var(--blog-border-primary)", background: "var(--blog-bg-secondary)", color: "var(--blog-text-primary)", cursor: "pointer", fontFamily: "inherit" }}
              >
                <User className="w-3 h-3" />
                Sign in
              </button>
            )}
          </div>
          {/* Mobile-only second row: nav links centered */}
          <div className="sm:hidden flex items-center justify-center gap-5 pt-3">
            <span className="uppercase" style={{ fontSize: 10, letterSpacing: "0.10em", color: "var(--blog-text-primary)" }}>About</span>
            <span style={{ color: "var(--blog-border-secondary)", fontSize: 10 }}>·</span>
            <Link to="/blog" className="uppercase" style={{ fontSize: 10, letterSpacing: "0.10em", color: "var(--blog-text-tertiary)" }}>Journal</Link>
            <span style={{ color: "var(--blog-border-secondary)", fontSize: 10 }}>·</span>
            <Link to="/blog/contact" className="uppercase" style={{ fontSize: 10, letterSpacing: "0.10em", color: "var(--blog-text-tertiary)" }}>Contact</Link>
          </div>
        </nav>

        {/* Header */}
        <header
          className="px-6 sm:px-12 lg:px-20 pt-10 pb-8"
          style={{ borderBottom: "0.5px solid var(--blog-border-tertiary)" }}
        >
          <div className="flex items-center gap-2 mb-3">
            <span
              className="inline-block"
              style={{ width: 18, height: "1.5px", background: "var(--blog-accent-teal)", flexShrink: 0 }}
            />
            <span
              className="font-mono uppercase"
              style={{ fontSize: 10, letterSpacing: "0.10em", color: "var(--blog-text-tertiary)" }}
            >
              About
            </span>
          </div>
          <h1
            className="font-display font-medium"
            style={{
              fontSize: "clamp(24px, 4vw, 38px)",
              lineHeight: 1.12,
              letterSpacing: "-0.02em",
              color: "var(--blog-text-primary)",
              maxWidth: 620
            }}
          >
            A Short Story.<br />
            <span style={{ color: "var(--blog-text-secondary)" }}>The Journey Continues.</span>
          </h1>
        </header>

        {/* Body */}
        <div className="px-6 sm:px-12 lg:px-20 pt-10 pb-4">
          <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr",
            gap: 40
          }}
        >
            {/* Two-col layout */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 300px), 1fr))",
                gap: 40,
                alignItems: "start"
              }}
            >
              {/* Left  -  text */}
              <div style={{ maxWidth: 580 }}>
                {/* Blogger-voice about text */}
                <div
                  className="blog-prose"
                  style={{
                    fontSize: 15,
                    lineHeight: 1.75,
                    color: "var(--blog-text-secondary)"
                  }}
                >
                  <p style={{ marginBottom: "1.4em" }}>
                    For those who know me, I'm glad to have you here. For those who don't, let's be friends :)
                  </p>
                  <p style={{ marginBottom: "1.4em" }}>
                    I'll make this easy.
                  </p>
                  <p style={{ marginBottom: "1.4em" }}>
                    My favorite color is red. I have four eyes (not the mythical kind). I enjoy reading, writing,
                    occasionally gaming, flirting with myself, and, above all, bringing ideas to life. If I'm being
                    honest, I could probably go on forever about the things I enjoy, but I suspect there's no need
                    for that.
                  </p>
                  <p style={{ marginBottom: "1.4em" }}>
                    I'll tell you a secret. Well, after this it won't really be a secret anymore. Or does it become a
                    public secret? Either way...
                  </p>
                  <p style={{ marginBottom: "1.4em" }}>
                    I'm highly anticipating meeting you. Regardless of where you're from or who you are, your
                    background is none of my business. I only care about one thing: what's inside your mind.
                  </p>
                  <p style={{ marginBottom: "1.4em" }}>
                    My aim is to connect with you, along with many others, in a personal and interactive way so that
                    we can all grow, learn, and reach new heights together.
                  </p>
                  <p style={{ marginBottom: "1.4em" }}>
                    Since this is an "About Me" section, I should probably get on with it.
                  </p>
                  <p style={{ marginBottom: "1.4em" }}>
                    I study Electrical and Computer Engineering at the University of Technology, Jamaica. In many
                    ways, the degree is almost secondary to what I'm actually doing; building.
                  </p>
                  <p style={{ marginBottom: "1.4em" }}>
                    Not prototypes. Not demos.
                  </p>
                  <p style={{ marginBottom: "1.4em" }}>
                    Systems that work. Systems that last. Systems that do something real.
                  </p>
                  <p style={{ marginBottom: "1.4em" }}>
                    I have a strong interest in finance, business, and an overwhelming craving for new knowledge. I
                    started this blog because I needed a place to think out loud. The walls of my room have grown
                    tired of listening to me.
                  </p>
                  <p style={{ marginBottom: "1.4em" }}>
                    I'm here to write about what it's actually like to build simple or complex systems, explore
                    technology, discuss the future, share life's challenges, and document the journey toward success.
                  </p>
                  <p style={{ marginBottom: "1.4em" }}>
                    This isn't a platform with a single voice or an overly dogmatic environment. Your comments,
                    thoughts, and beliefs are all welcome here.
                  </p>
                  <p style={{ marginBottom: "1.4em" }}>
                    If you read something and disagree, I encourage you to share your perspective. In fact, I'd
                    challenge you to learn more than me and then come back and teach me something in the comments or
                    through my contact page. I welcome conversation. I welcome challenges.
                  </p>
                  <p style={{ marginBottom: "1.4em" }}>
                    Thank you for choosing to support me on this journey.
                  </p>
                  <p style={{ marginBottom: "1.4em" }}>
                    May whatever God, deity, or higher power you believe in bless you with health, strength, wisdom,
                    patience, and wealth. For your sake and mine. 😉
                  </p>
                  <p style={{ marginBottom: "1.4em" }}>
                    This isn't the kind of writing that trends. At least, not yet.
                  </p>
                  <p style={{ marginBottom: "1.4em" }}>
                    But it's honest.
                  </p>
                  <p style={{ marginBottom: "1.4em" }}>
                    And I think that matters more.
                  </p>
                  <p style={{ marginBottom: "0.6em" }}>
                    Yours truly,
                  </p>
                  <p style={{ marginBottom: "1.4em" }}>
                    Malicke 🤍
                  </p>
                  <p
                    className="font-mono uppercase"
                    style={{
                      fontSize: 11,
                      letterSpacing: "0.08em",
                      color: "var(--blog-accent-magenta)",
                      marginBottom: "1.4em"
                    }}
                  >
                    Proverbs 27:17
                  </p>
                </div>

                {/* CTA buttons */}
                <div className="flex items-center gap-3 flex-wrap mt-8 mb-10">
                  <Link
                    to="/blog/contact"
                    className="inline-flex items-center gap-1.5 rounded-md"
                    style={{
                      background: "transparent",
                      color: "var(--blog-text-secondary)",
                      border: "0.5px solid var(--blog-border-primary)",
                      padding: "11px 20px",
                      fontSize: 12,
                      fontWeight: 500,
                      letterSpacing: "0.04em",
                      textDecoration: "none"
                    }}
                  >
                    Get in touch
                  </Link>
                </div>
              </div>

            </div>
          </div>
        </div>

      </div>

      {/* Bottom */}
      <div
        className="mx-auto mt-4 flex items-center justify-between flex-wrap gap-3"
        style={{ width: "100%", maxWidth: 980 }}
      >
        <Link
          to="/blog"
          className="font-mono uppercase inline-flex items-center gap-1"
          style={{ fontSize: 10, letterSpacing: "0.14em", color: "var(--blog-text-tertiary)" }}
        >
          ← Back to Journal
        </Link>
        <a
          href="/feed.xml"
          className="font-mono uppercase inline-flex items-center gap-1"
          style={{ fontSize: 10, letterSpacing: "0.14em", color: "var(--blog-text-tertiary)" }}
        >
          RSS feed
          <ArrowUpRight className="w-3 h-3" />
        </a>
      </div>

      <AuthModal
        open={authOpen}
        onClose={() => setAuthOpen(false)}
        defaultMode="login"
      />
    </div>
  );
}
